import { useState } from "react";

import { Icon } from "@/lib/components/ui/Icon/Icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/ui/Popover";

import styles from "./Select.module.scss";

export type SelectOptionProps<T> = {
  label: string;
  value: T;
  disabled?: boolean;
  hint?: string;
};

type SingleSelectProps<T> = {
  multiple?: false;
  value?: T;
  onChange: (option: T) => void;
};

type MultiSelectProps<T> = {
  multiple?: boolean;
  value?: T[];
  onChange: (option: T[]) => void;
};

type BaseSelectProps<T> = {
  options: SelectOptionProps<T>[];
  dataTest?: string;
  label?: string;
  fieldLabel?: string;
  readOnly?: boolean;
  className?: string;
  emptyLabel?: string;
  popoverClassName?: string;
  popoverSide?: "top" | "bottom" | "left" | "right" | undefined;
  popoverAlign?: "start" | "center" | "end" | undefined;
  variant?: "light" | "dark";
  small?: boolean;
  triggerLabelFormatter?: (selectedOptions: SelectOptionProps<T>[]) => string;
};

export type SelectProps<T> = BaseSelectProps<T> &
  (SingleSelectProps<T> | MultiSelectProps<T>);

export const Select = <T extends string | number>(
  props: SelectProps<T>
): JSX.Element => {
  const {
    options,
    dataTest,
    label,
    fieldLabel,
    readOnly = false,
    emptyLabel,
    multiple,
    variant = "light",
    small,
    popoverClassName,
    popoverSide = "bottom",
    popoverAlign = "start",
    triggerLabelFormatter,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const isMultiple = multiple === true;

  const selectedValues: T[] = isMultiple
    ? (props as MultiSelectProps<T>).value ?? []
    : (props as SingleSelectProps<T>).value != null
    ? [(props as SingleSelectProps<T>).value!]
    : [];

  const selectedOptions = options.filter((o) =>
    selectedValues.includes(o.value)
  );

  const selectedLabels = selectedOptions.map((o) => o.label);

  const displayLabel =
    selectedOptions.length > 0
      ? triggerLabelFormatter?.(selectedOptions) ?? selectedLabels.join(", ")
      : undefined;

  const handleOptionClick = (optionValue: T) => {
    if (isMultiple) {
      const multiOnChange = (props as MultiSelectProps<T>).onChange;
      const current = (props as MultiSelectProps<T>).value ?? [];
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      multiOnChange(next);
    } else {
      (props as SingleSelectProps<T>).onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={`${styles.select_container} ${styles[variant]} ${
          small ? styles.small : ""
        }`}
      >
        {fieldLabel && (
          <div className={styles.field_caption}>
            <span className={styles.field_name}>{fieldLabel}</span>
          </div>
        )}
        {label !== undefined && <span className={styles.label}>{label}</span>}
        <PopoverTrigger asChild disabled={readOnly}>
          <button
            type="button"
            data-test={dataTest}
            className={`${styles.trigger} ${isOpen ? styles.open : ""} ${
              readOnly ? styles.readonly : ""
            } ${!displayLabel ? styles.placeholder : ""}  ${
              props.className ?? ""
            }`}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            disabled={readOnly}
          >
            <span className={`${styles.trigger_text} `}>
              {displayLabel ?? emptyLabel ?? "-"}
            </span>
            <span
              className={`${styles.trigger_icon} ${isOpen ? styles.rotated : ""}
              }`}
            >
              <Icon name="chevronDown" size="small" color="inherit" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={popoverSide}
          align={popoverAlign}
          className={`${styles.dropdown} ${small ? styles.small : ""} ${
            popoverClassName ?? ""
          }`}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);

            return (
              <button
                type="button"
                key={option.value}
                data-test={
                  dataTest
                    ? `${dataTest}-option-${String(option.value).replace(/_/g, "-")}`
                    : undefined
                }
                className={`${styles.option} ${
                  isSelected ? styles.selected : ""
                } ${option.disabled ? styles.disabled : ""}`}
                title={option.hint}
                aria-disabled={option.disabled}
                onClick={() => {
                  if (!option.disabled) handleOptionClick(option.value);
                }}
              >
                {isMultiple && (
                  <div
                    className={`${styles.checkbox} ${
                      isSelected ? styles.checked : ""
                    }`}
                  >
                    {isSelected && (
                      <Icon name="check" size="small" color="primary" />
                    )}
                  </div>
                )}
                <span className={styles.option_label}>{option.label}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <div className={styles.empty_state}>{"No options available"}</div>
          )}
        </PopoverContent>
      </div>
    </Popover>
  );
};
